// One lifecycle for every background poller in the console.
//
// Hand-rolled setInterval() in views produced a class of bug that is invisible
// in the browser but very visible in the production log:
//
//   1. AN INTERVAL REGISTERED AFTER THE COMPONENT WAS ALREADY GONE. Dashboard.vue
//      created its /system/status timer at the END of an ASYNC onMounted(), after
//      three awaited requests. If the view unmounted while those were in flight
//      (a 401 recovery, a nav click, a logout), onUnmounted() ran first — with the
//      timer variable still undefined, so it cleared nothing — and the awaited
//      continuation then created a timer that NO unmount hook owns any more. The
//      1.26.4 production log shows exactly that: `GET /system/status 401` once a
//      minute, forever, with `Referer: https://admin.comply-it.net/login` — a
//      poller belonging to a Dashboard that had been unmounted 20 minutes earlier.
//   2. POLLING A HIDDEN TAB. A backgrounded tab kept fetching (browsers only
//      throttle it to once a minute), which is how a stale tab keeps hammering an
//      endpoint nobody is looking at.
//   3. OVERLAPPING RUNS. /system/status takes ~540 ms and /instances/ is 29 KB;
//      a slow answer must not have a second request stacked on top of it.
//
// createPoller() is the dependency-injected core (no Vue, no DOM) so those rules
// can be unit-tested (tests/poller.test.ts); usePoller() binds one to a
// component's lifetime.

import { getCurrentInstance, onBeforeUnmount } from 'vue'

export interface PollerDeps {
    setInterval:   (fn: () => void, ms: number) => unknown
    clearInterval: (handle: unknown) => void
    // True while the document is hidden (backgrounded tab): the timer keeps
    // ticking but does no work.
    isHidden:      () => boolean
}

export interface Poller {
    /** Register the timer. A second call while running is a no-op, and a call
     *  after dispose() (i.e. after unmount) is refused outright. */
    start(): void
    /** Clear the timer. start() can bring it back. */
    stop(): void
    /** Permanently retire the poller: clears the timer and refuses any later
     *  start(), so an async continuation cannot resurrect it. */
    dispose(): void
    /** Run once now, honouring the in-flight guard. */
    trigger(): Promise<void>
    /** Test/debug accessors. */
    isRunning(): boolean
    isInFlight(): boolean
}

export const browserPollerDeps = (): PollerDeps => ({
    setInterval:   (fn, ms) => setInterval(fn, ms),
    clearInterval: handle => clearInterval(handle as ReturnType<typeof setInterval>),
    isHidden:      () => typeof document !== 'undefined' && document.hidden,
})

/**
 * createPoller wires one repeating task to one timer.
 *
 * Invariants (all covered by tests/poller.test.ts):
 *   * at most ONE timer exists at any moment, however often start() is called,
 *   * stop()/dispose() always clear the timer they created,
 *   * after dispose() no timer can ever be created again,
 *   * a tick is skipped while the tab is hidden or a previous run is in flight,
 *   * a run that throws never kills the timer.
 */
export function createPoller(run: () => unknown, intervalMs: number, deps: PollerDeps): Poller {
    let handle: unknown
    let inFlight = false
    let disposed = false

    const trigger = async (): Promise<void> => {
        if (disposed || inFlight) return
        inFlight = true
        try {
            await run()
        } catch {
            // A failed poll is just a missed refresh — it must never take the
            // timer (or the view) down with it.
        } finally {
            inFlight = false
        }
    }

    const tick = () => {
        if (deps.isHidden()) return
        void trigger()
    }

    const stop = () => {
        if (handle === undefined) return
        deps.clearInterval(handle)
        handle = undefined
    }

    return {
        start() {
            if (disposed || handle !== undefined) return
            handle = deps.setInterval(tick, intervalMs)
        },
        stop,
        dispose() {
            disposed = true
            stop()
        },
        trigger,
        isRunning: () => handle !== undefined,
        isInFlight: () => inFlight,
    }
}

/**
 * usePoller returns a poller bound to the calling component: it is disposed in
 * onBeforeUnmount, so neither a leftover timer nor a late async start() can
 * survive the view. Returning to a visible tab refreshes immediately instead of
 * waiting out the remainder of the period.
 */
export function usePoller(run: () => unknown, intervalMs: number): Poller {
    const poller = createPoller(run, intervalMs, browserPollerDeps())

    const onVisibility = () => {
        if (!document.hidden) void poller.trigger()
    }

    if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', onVisibility)
    }

    // Outside a component (should not happen in this app) the poller is simply
    // never auto-disposed — better than throwing at setup time.
    if (getCurrentInstance()) {
        onBeforeUnmount(() => {
            if (typeof document !== 'undefined') {
                document.removeEventListener('visibilitychange', onVisibility)
            }
            poller.dispose()
        })
    }

    return poller
}
